<template>
  <div class="send-campaign">
    <Editor
      ref="form"
      class="send-campaign-notification"
      saveBtnI18nKey="common.btn.send"
      :loading.sync="loading"
      :title="$t('admin.crm.label.campaignNotification.header', { name: campaign.name || '' })"
      :model="campaignNotification"
      :formLines="formLines"
      @cancel="handleCancel"
      @save="handleSave"
    >
      <template #talentGroupId="{ dataKey }">
        <el-select
          v-model="campaignNotification[dataKey]"
          filterable
          :no-match-text="$t('admin.crm.message.campaignNotification.noMatchText')"
          :no-data-text="$t('admin.crm.message.campaignNotification.noDataText')"
          :placeholder="$t('admin.crm.placeholder.campaignNotification.talentGroup')"
        >
          <el-option
            v-for="item in talentGroupList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </template>

      <template #sendAlumniType="{ dataKey }">
        <el-select
          v-model="campaignNotification[dataKey]"
          filterable
          :no-match-text="$t('admin.crm.message.campaignNotification.noMatchText')"
          :no-data-text="$t('admin.crm.message.campaignNotification.noDataText')"
          :placeholder="$t('admin.crm.placeholder.campaignNotification.alumniType')"
        >
          <el-option
            v-for="item in sendAlumniType"
            :key="item.value"
            :label="$t(item.nameI18nKey)"
            :value="item.value"
          />
        </el-select>
      </template>

      <template #messageType="{ dataKey }">
        <el-select v-model="campaignNotification[dataKey]" :disabled="alumniTemplateVisibility" @change="handleMessageTypeChange">
          <el-option
            v-for="(item, key) in messageTypeOptions"
            :key="key"
            :label="$t(item.i18nKey)"
            :value="item.value"
          />
        </el-select>
      </template>
      <template #messageId="{ dataKey }">
        <el-select
          v-model="campaignNotification[dataKey]"
          filterable
          :filter-method="searchNotificationMessages"
          :no-match-text="$t('admin.crm.message.campaignNotification.noMatchText')"
          :no-data-text="$t('admin.crm.message.campaignNotification.noDataText')"
          :placeholder="$t('admin.crm.placeholder.campaignNotification.message')"
          @change="handleMessageChange"
        >
          <el-option
            v-for="item in messageList"
            :key="item.id"
            :label="item.title"
            :value="item.id"
          />
        </el-select>
      </template>
      <template #portal>
        <i class="portal-btn iconfont iconCompaign-copyurl" :title="$t('admin.crm.btn.campaign.copyURL')" @click="handleCopyLink"></i>
        <i class="portal-btn iconfont iconCompaign-qrcode" :title="$t('admin.crm.btn.campaign.qrCode')" @click="handleOpenQrCode"></i>
      </template>
      <template #messagePreview>
        <div class="message-content">
          <div class="wechat-preview preview-content" v-if="needSendWeChat">
            <div class="message-type-placeholder">
              <div>
                <span class="icon iconfont iconCRM-wechat message-type-icon"></span>
              </div>
              <div class="message-type-text">
                {{ $t('admin.crm.placeholder.campaignNotification.wechatPreview') }}
              </div>
            </div>
            <div class="message-content" v-if="message.id">
              <div class="message-title">
                {{ message.title }}
              </div>
              <div class="message-thumb">
                <img :src="message.thumbUrl" width="100%" height="100%"/>
              </div>
              <div class="message-subject">
                {{ message.subject }}
              </div>
            </div>
          </div>
          <div class="email-preview preview-content" v-if="needSendEmail">
            <div class="message-type-placeholder">
              <div>
                <span class="icon iconfont iconCRM-email message-type-icon"></span>
              </div>
              <div class="message-type-text">
                {{ $t('admin.crm.placeholder.campaignNotification.emailPreview') }}
              </div>
            </div>
            <div class="email-content">
              <quill-editor
                v-model="campaignNotification.emailContent"
                ref="quillEditor"
                class="email-content-editor"
                :options="quillEditorOption" />
            </div>
            <div ref="workSpace" class="work-space"></div>
          </div>
        </div>
      </template>
      <template #page-bottom>
        <QrCodeDialog v-bind="qrCodeDialog" :show.sync="qrCodeDialog.show" />
      </template>
    </Editor>
    <ConfirmDialog
      class="confirm-send-dialog"
      :title="$t(confirmSendDialogInfo.title)"
      :content="$t(confirmSendDialogInfo.content, { groupName: confirmSendDialogInfo.talentGroupName, totalCount: formatBigNumber(confirmSendDialogInfo.totalCount) })"
      :show.sync="confirmSendDialogInfo.show"
      :loading="confirmSendDialogInfo.loading"
      @confirm="handleConfirmSend"
      @cancel="handleCancelSend"
    >
    </ConfirmDialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { MESSAGE_TYPE } from '@/constant/crm'
import QrCodeDialog from '@/components/dialog/qrCode.vue'
import Editor from '../editor.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import { formatBigNumber } from '@/components/filters'
import { ROOT_COMPANY_ID } from '@/constant/company'

const includeKeyList = [
  'margin',
  'padding',
  'border',
  'background',
  'color',
  'fontSize',
  'fontFamily',
  'lineHeight',
  'textAlign',
  'textDecoration',
  'direction',
  'boxSizing',
  'listStyleType',
  'verticalAlign'
]

export default {
  components: {
    Editor,
    QrCodeDialog,
    ConfirmDialog
  },
  metaInfo: {
    meta: [{
      name: 'referrer',
      content: 'never'
    }]
  },
  data () {
    return {
      formatBigNumber,
      loadingCount: 0,
      campaign: {},
      alumniTemplateVisibility: false,
      sendAlumniType: [
        { value: 'IN_TALENT_POOL', nameI18nKey: 'admin.crm.option.alumniSearchTypeOption.InTalentPool' },
        { value: 'NOT_IN_TALENT_POOL', nameI18nKey: 'admin.crm.option.alumniSearchTypeOption.notInTalentPool' },
        { value: 'ALL_ALUMNI', nameI18nKey: 'admin.crm.option.alumniSearchTypeOption.allInTalentPool' }
      ],
      campaignNotification: {
        messageId: '',
        messageType: MESSAGE_TYPE.EMAIL.value,
        talentGroupId: '',
        emailSubject: '',
        emailContent: ''
      },
      talentGroupList: [],
      messageList: [],
      messageBackupList: [],
      messageTotalCount: 0,
      messagePageNumber: 1,
      messagePageSize: 20,
      messageTypeOptions: MESSAGE_TYPE,
      message: {
        id: '',
        title: '',
        subject: '',
        thumbUrl: ''
      },
      quillEditorOption: {
        placeholder: this.$t('admin.crm.placeholder.campaignNotification.emailContent'),
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['link', 'image'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            [{ color: [] }, { background: [] }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }]
          ]
        }
      },
      qrCodeDialog: {
        show: false,
        headerLabel: '',
        qrImageName: '',
        qrLogo: '',
        qrLogoScale: 0.23,
        qrData: '',
        qrSize: 500
      },
      confirmSendDialogInfo: {
        show: false,
        title: 'admin.crm.label.campaignNotification.confirmSendDialogTitle',
        content: 'admin.crm.label.campaignNotification.confirmSendDialogContent',
        notification: null,
        loading: false,
        talentGroupName: '',
        totalCount: null
      }
    }
  },
  computed: {
    ...mapGetters('account', ['getCurrentAccount']),
    loading: {
      get () {
        return this.loadingCount > 0
      },
      set (val) {
        this.loadingCount += val ? 1 : -1
      }
    },
    campaignId () {
      return this.$route.params.campaignId
    },
    formLines () {
      const lines = {
        talentGroupId: {
          hidden: this.alumniVisibility,
          labelI18nKey: 'admin.crm.label.campaignNotification.talentGroup',
          rules: [{ required: true, message: this.$t('admin.crm.message.campaignNotification.talentGroupRequired'), trigger: 'change' }]
        },
        sendAlumniType: {
          hidden: !this.alumniVisibility,
          labelI18nKey: 'admin.crm.label.campaignNotification.alumniSearchType'
        },
        messageType: {
          labelI18nKey: ('admin.crm.label.campaignNotification.messageType')
        },
        messageId: {
          hidden: !this.needSendWeChat,
          labelI18nKey: 'admin.crm.label.campaignNotification.message',
          rules: [
            { required: true, message: this.$t('admin.crm.message.campaignNotification.messageRequired'), trigger: 'change' },
            { validator: this.validateMessageId, message: this.$t('admin.crm.message.campaignNotification.messageRequired'), trigger: 'change' }
          ]
        },
        emailSubject: {
          hidden: !this.needSendEmail,
          labelI18nKey: 'admin.crm.label.campaignNotification.emailSubject',
          placeholderI18nKey: 'admin.crm.placeholder.campaignNotification.emailSubject',
          rules: [{ required: true, message: this.$t('admin.crm.message.campaignNotification.emailContentRequired'), trigger: 'blur' }]
        },
        portal: {
          labelI18nKey: 'admin.crm.label.campaignNotification.portal',
          showRequiredAsterisk: true
        },
        messagePreview: {
          labelI18nKey: this.needSendWeChat ? 'admin.crm.label.campaignNotification.messagePreview' : 'admin.crm.label.campaignNotification.emailContent'
        }
      }

      return lines
    },
    needSendEmail () {
      return this.campaignNotification.messageType === this.messageTypeOptions.WECHAT_AND_EMAIL.value || this.campaignNotification.messageType === this.messageTypeOptions.EMAIL.value
    },
    needSendWeChat () {
      return this.campaignNotification.messageType === this.messageTypeOptions.WECHAT_AND_EMAIL.value || this.campaignNotification.messageType === this.messageTypeOptions.WECHAT.value
    },
    alumniVisibility () {
      return this.alumniTemplateVisibility
    }
  },
  created () {
    this.loading = true
    Promise.all([
      this.initCampaign(),
      this.fetchTalentGroupList(),
      this.searchNotificationMessages('')
    ]).finally(() => {
      this.loading = false
    })
  },
  methods: {
    ...mapActions('crm', ['getCampaignById', 'searchCampaignNotificationMessage', 'sendCampaignNotification']),
    ...mapActions('talent', ['searchTalentFilterGroupList']),
    async initCampaign () {
      const resp = await this.getCampaignById({ campaignId: this.campaignId, params: { need_company: true } })
      if (resp) {
        this.campaign = resp
        if (resp.microsite.visibility === 'A') {
          this.alumniTemplateVisibility = true
        }
      }
    },
    async fetchTalentGroupList () {
      const resp = await this.searchTalentFilterGroupList({})
      if (resp) {
        this.talentGroupList = resp
      }
    },
    async fetchNotificationMessages () {
      this.loading = true
      try {
        const params = {
          page_number: this.messagePageNumber,
          page_size: this.messagePageSize
        }
        if (!this.needSendWeChat) {
          return
        }
        const resp = await this.searchCampaignNotificationMessage(params)
        if (resp) {
          this.messageTotalCount = resp.total_count
          _.map(resp.item, messageItem => {
            const message = {
              id: messageItem.media_id
            }
            const newsList = messageItem.content.news_item
            if (newsList.length > 0) {
              const news = newsList[0]
              message.title = news.title
              message.subject = news.digest
              message.thumbUrl = news.thumb_url
              this.messageBackupList.push(message)
            }
          })
        }
      } finally {
        this.loading = false
      }
    },
    async searchNotificationMessages (keyword) {
      if (keyword !== '') {
        const matchedList = this.messageBackupList.filter(item => {
          return item.title.toLocaleLowerCase().indexOf(keyword) > -1
        })
        if (this.messageTotalCount > this.messagePageNumber * this.messagePageSize && matchedList.length < this.messagePageSize) {
          this.messagePageNumber += 1
          await this.fetchNotificationMessages()
          await this.searchNotificationMessages(keyword)
        } else {
          this.messageList = matchedList
        }
      } else {
        this.messagePageNumber = 1
        this.messageBackupList = []
        await this.fetchNotificationMessages()
        this.messageList = this.messageBackupList
      }
    },
    handleMessageTypeChange () {
      this.fetchNotificationMessages()
    },
    handleMessageChange () {
      const selectedMessage = this.messageBackupList.filter(item => item.id === this.campaignNotification.messageId)
      if (selectedMessage.length > 0) {
        this.message = selectedMessage[0]
      }
    },
    deepStyleFormat (originChildren, targetChildren) {
      _.forEach(originChildren, (child, index) => {
        const targetChild = targetChildren[index]
        if (!child || !targetChild) {
          return true
        }
        const originStyles = window.getComputedStyle(child, null)
        const targetStyles = window.getComputedStyle(targetChild, null)
        _.forEach(includeKeyList, (key) => {
          const originValue = originStyles[key]
          const targetValue = targetStyles[key]
          if (originValue !== targetValue) {
            targetChild.style[key] = originValue
          }
        })
        if (child.children && child.children.length > 0) {
          this.deepStyleFormat(child.children, targetChild.children)
        }
      })
    },
    validateMessageId (rule, value, callback) {
      if (this.needSendWeChat && !value) {
        callback(rule.message)
      } else {
        callback()
      }
    },
    generateEmail () {
      const workSpace = this.$refs.workSpace
      const quillEditor = this.$refs.quillEditor
      const editor = quillEditor.$el.querySelector('.ql-editor')
      workSpace.innerHTML = this.campaignNotification.emailContent
      this.deepStyleFormat(editor.children, workSpace.children)
      const emailContent = workSpace.innerHTML

      return emailContent
    },
    handleCancel () {
      const nextPath = this.$route.query.back || '/v-admin/crm-management/campaign-management/list'
      this.$router.push(nextPath)
    },
    async handleSave (closeLoading) {
      let emailContent = this.campaignNotification.emailContent
      if (this.needSendEmail) {
        emailContent = this.generateEmail()
        if (!emailContent) {
          this.$message.error(this.$t('admin.crm.message.campaignNotification.emailContentRequired'))
          closeLoading()
          return
        }
      }
      this.confirmSendDialogInfo.notification = {
        ...this.campaignNotification,
        emailContent: emailContent
      }
      const talentGroup = _.find(this.talentGroupList, item => item.id === this.campaignNotification.talentGroupId)
      this.confirmSendDialogInfo.totalCount = talentGroup && talentGroup.report ? talentGroup.report.total_count : 0
      this.confirmSendDialogInfo.talentGroupName = talentGroup.name
      this.confirmSendDialogInfo.show = true
      closeLoading()
    },
    async handleConfirmSend () {
      this.confirmSendDialogInfo.loading = true
      try {
        const resp = await this.sendCampaignNotification({ campaignId: this.campaignId, notification: this.confirmSendDialogInfo.notification })
        if (resp) {
          this.$message.success(this.$t('admin.crm.message.campaignNotification.notificationSent'))
          this.handleCancel()
        } else {
          this.$message.error(this.$t('admin.crm.message.campaignNotification.notificationSentFailed'))
        }
      } finally {
        this.confirmSendDialogInfo.loading = false
      }
    },
    handleCancelSend () {
      this.confirmSendDialogInfo.show = false
    },
    handleCopyLink () {
      let origin = window.location.origin
      if (this.getCurrentAccount.root_company_id === ROOT_COMPANY_ID.MERCEDES_BENZ) {
        origin = process.env.VUE_APP_MERCEDES_BENZ_URL
      }
      const link = `${origin}/recruiting/crm/campaign/${this.campaign.short_unique_id}`
      prompt(this.$t('admin.crm.label.campaign.copyLink'), link)
    },
    handleOpenQrCode () {
      let origin = window.location.origin
      if (this.getCurrentAccount.root_company_id === ROOT_COMPANY_ID.MERCEDES_BENZ) {
        origin = process.env.VUE_APP_MERCEDES_BENZ_URL
      }
      const qrCodeDialog = this.qrCodeDialog
      qrCodeDialog.qrData = `${origin}/recruiting/crm/campaign/${this.campaign.short_unique_id}`
      qrCodeDialog.headerLabel = this.campaign.name
      qrCodeDialog.qrImageName = this.campaign.name
      qrCodeDialog.qrLogo = this.campaign.company.logo
      qrCodeDialog.show = true
    }
  }
}
</script>

<style lang="scss" scoped>
.send-campaign {
  .send-campaign-notification {
    .preview-content {
      border: 1px solid #e4e7ed;
      min-height: 70px;

      .message-type-placeholder {
        background-color: #f5f5f5;
        padding-left: 20px;
        display: flex;
        align-items: center;
        color: #303133;

        .message-type-icon {
          font-size: 20px;
        }

        .message-type-text {
          font-size: 16px;
          margin-left: 5px;
        }
      }
    }

    .wechat-preview {
      margin-bottom: 20px;

      .message-content {
        padding: 10px 20px;

        .message-title {
          font-size: 18px;
          font-weight: bold;
        }
      }
    }

    .email-preview {
      .email-content {
        min-height: 300px;
        color: #303133;

        .email-content-editor {
          ::v-deep {
            .ql-toolbar.ql-snow {
              padding-left: 25px;
              line-height: initial;
              border: none;
            }

            .ql-container.ql-snow {
              border: none;

              .ql-editor {
                img {
                  vertical-align: bottom;
                }
              }
            }
          }
        }
      }

      .work-space {
        display: none;
      }
    }

    .portal-btn {
      margin: 0 10px;
      font-size: 28px;
      color: #0296cc;
      cursor: pointer;

      &:first-child {
        margin-left: -6px;
      }
    }
  }
  .confirm-send-dialog {
    ::v-deep {
      .el-dialog {
        width: 40%;
      }
    }
  }
}
</style>
